// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Tooltip } from '@mui/material';
import type { AccountTransaction } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { Table } from '@shared/ui-kit';
import { classNames } from '@shared/ui-kit/utils/classnames';
import { createColumnHelper, type Row } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTransactionLabels } from '../../hooks/use-transaction-labels';

export interface TransactionsTableProps {
  readonly data: AccountTransaction[];
  readonly onRowClick?: (transaction: AccountTransaction) => void;
}

const columnHelper = createColumnHelper<AccountTransaction>();

const getRowLabel = (transaction: AccountTransaction) =>
  transaction.description || transaction.name || transaction.type || '-';

export const useTransactionsTable = ({
  data: _data,
  onRowClick,
}: TransactionsTableProps) => {
  const { t } = useTranslation();

  const { getTypeLabel, getSubtypeLabel } = useTransactionLabels();

  const selectedCurrency = useSelector(selectAccountCurrency);

  const handleRowClick = useCallback(
    (row: Row<AccountTransaction>) => {
      onRowClick?.(row.original);
    },
    [onRowClick],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('date', {
        header: t('common.date', { ns: 'common' }),
        sortingFn: 'datetime',
        cell: info => <Table.Data format="date" value={info.getValue()} />,
      }),

      columnHelper.accessor('type', {
        header: t('common.type', { ns: 'common' }),
        cell: info => <span>{getTypeLabel(info.getValue())}</span>,
      }),

      columnHelper.accessor('subtype', {
        header: t('common.category', { ns: 'common' }),
        cell: info => <span>{getSubtypeLabel(info.getValue())}</span>,
      }),

      columnHelper.accessor(row => getRowLabel(row), {
        id: 'label',
        header: t('common.description', { ns: 'common' }),
        cell: info => (
          <Tooltip title={info.getValue()} placement="top">
            <span className="max-w-[320px] truncate">{info.getValue()}</span>
          </Tooltip>
        ),
      }),

      columnHelper.accessor('amount', {
        header: t('common.amount', { ns: 'common' }),
        meta: { alignment: 'end' },
        cell: info => (
          <span
            className={classNames({
              'text-primary-green': (info.getValue() ?? 0) >= 0,
              'text-primary-red': (info.getValue() ?? 0) < 0,
            })}
          >
            <Table.Data
              format="currency"
              validate
              value={info.getValue()}
              currency={selectedCurrency}
            />
          </span>
        ),
      }),
    ],
    [getSubtypeLabel, getTypeLabel, selectedCurrency, t],
  );

  return {
    columns,
    onRowClick: onRowClick ? handleRowClick : undefined,
  };
};
